import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { API_URL, IHsaOverviewResponseDto, INotionalOverviewResponseDto } from '@/dtos';

import { AccountStatisticPayload } from '../../EmployerDashboard.types';
import { EmployerPlanStatisticPayload, EmployerPlanStatisticTierPayload } from '../EmployerStatistics.types';

interface Params {
  planId?: string;
  isHSA?: boolean;
  enabled?: boolean;
}
const useGetEmployerStatisticsQuery = ({ planId, isHSA, enabled }: Params) => {
  const KEY = isHSA
    ? API_URL.ACCOUNT_USAGE_PLANS_OVERVIEW_HSA_BY_PLAN_ID('')
    : API_URL.ACCOUNT_USAGE_PLANS_OVERVIEW_NOTIONAL_BY_PLAN_ID('');
  const URL = isHSA
    ? API_URL.ACCOUNT_USAGE_PLANS_OVERVIEW_HSA_BY_PLAN_ID(planId || '')
    : API_URL.ACCOUNT_USAGE_PLANS_OVERVIEW_NOTIONAL_BY_PLAN_ID(planId || '');
  const { data, isLoading, isSuccess } = useQuery([isHSA, KEY, planId], () => api.get(URL), {
    enabled,
  });
  const formattedData = useMemo<AccountStatisticPayload | null>(() => {
    const response = data?.data as INotionalOverviewResponseDto;
    if (!data?.data) {
      return null;
    }
    if (isHSA) {
      const hsaResponse = response as IHsaOverviewResponseDto;
      const formatted: AccountStatisticPayload = {
        activeEnrollments: hsaResponse.active_enrollments || 0,
        totalElections: hsaResponse?.total_elections || 0,
        unenrolledHSAs: hsaResponse.unenrolled_hsas || 0,
        investmentBalance: hsaResponse.investment_balance,
        investmentParticipants: hsaResponse.investment_participants || 0,
        planYearContributions: hsaResponse.plan_year_contributions || 0,
      };
      return formatted;
    }
    const formatted: AccountStatisticPayload = {
      activeEnrollments: response.active_enrollments || 0,
      canceledEnrollments: response.canceled_enrollments || 0,

      totalElections: response?.total_elections || 0,
      rolloverIn: response.rollover_in,
      rolloverOut: response.rollover_out,
      limitedUseCount: response.limited_use_count || 0,
      planYearContributions: response.plan_year_contributions || 0,
      deductibleTracking: response.deductible_tracking,
      plans:
        response?.deductible_tracking?.map(
          (item): EmployerPlanStatisticPayload => ({
            planId: item.health_plan_id || 0,
            planName: item.health_plan_name || '',
            tiers:
              item?.tiers?.map(
                (tierItem): EmployerPlanStatisticTierPayload => ({
                  name: tierItem.tier_name || '',
                  familyCount: tierItem.family_count || 0,
                })
              ) || [],
          })
        ) || [],
    };
    return formatted;
  }, [data?.data, isHSA]);
  return {
    data: formattedData,
    isLoading,
    isSuccess,
  };
};

export default useGetEmployerStatisticsQuery;
